import { useEffect, useId, useMemo } from 'react';
import { useIntl } from 'react-intl';

import type { SelectOption } from '@/components/Select';
import { APOLLO_CLIENTS } from '@/graphql/client';
import {
  useStorefrontReturnReasonsQuery,
  useStorefrontWithdrawalEligibilityQuery,
} from '@/graphql/orderApp/api';
import type { LanguageCodeEnum } from '@/graphql/saleor/api';
import { useImageLoader } from '@/hooks/useImageLoader';
import { useIsSmDown } from '@/hooks/useMedia';
import { useRouter } from '@/hooks/useRouter';
import { useUser } from '@/hooks/useUser';
import { BRAND, WITHDRAWAL_ENABLED } from '@/lib/const';
import { toLocalizedStorefrontReasonOptions } from '@/lib/withdrawal/reasons';
import { useRegionProvider } from '@/providers/RegionProvider';
import {
  resolveOrderDetailsDisplayLines,
  sortLines,
  useOrderDetails,
} from '@/views/OrderDetailsView/helpers';

import type { OrderReturnViewProps } from './orderReturnTypes';
import { isReturnableLine } from './orderReturnUtils';
import { useGuestReturnReasons } from './useGuestReturnReasons';
import { useOrderReturnState } from './useOrderReturnState';

export function useReturnViewData({
  guestAuthContext,
  mode = 'registered',
  onGuestCancel,
  onGuestEligibilityChange,
  order: orderProp,
  orderId,
  withdrawalEligibilityOverride,
}: OrderReturnViewProps) {
  const { loader: imageLoader } = useImageLoader();
  const { paths, push } = useRouter();
  const {
    snapshot: { user },
  } = useUser();
  const {
    country: { channelSlug },
    query: { languageCode },
  } = useRegionProvider();
  const isGuestMode = mode === 'guest' || Boolean(guestAuthContext);
  const isWithdrawalFeatureDisabled = !WITHDRAWAL_ENABLED;
  const isMobileView = useIsSmDown();
  const { formatMessage } = useIntl();
  const baseFieldId = useId();

  const resolvedOrderId = isGuestMode ? undefined : orderId;

  const state = useOrderDetails({
    order: orderProp,
    orderId: resolvedOrderId,
    languageCode: languageCode as LanguageCodeEnum,
    user,
  });

  const orderAppOrderId = state.order?.id;

  const {
    data: withdrawalEligibilityData,
    loading: isWithdrawalEligibilityLoading,
    refetch: refetchWithdrawalEligibility,
  } = useStorefrontWithdrawalEligibilityQuery({
    variables: {
      input: {
        brand: BRAND.toUpperCase(),
        orderId: orderAppOrderId,
      },
    },
    skip:
      !WITHDRAWAL_ENABLED ||
      isGuestMode ||
      Boolean(withdrawalEligibilityOverride) ||
      !orderAppOrderId,
    context: { client: APOLLO_CLIENTS.orderApp },
  });

  const { data: returnReasonsData } = useStorefrontReturnReasonsQuery({
    skip: !WITHDRAWAL_ENABLED || isGuestMode,
    context: { client: APOLLO_CLIENTS.orderApp },
  });

  const guestReasons = useGuestReturnReasons(isGuestMode);

  const orderLines = state.order
    ? resolveOrderDetailsDisplayLines(state.order)
    : [];
  const sortedLines = sortLines(orderLines, true);
  const returnableLines = sortedLines.filter(isReturnableLine);

  const reasonOptions: SelectOption[] = useMemo(() => {
    const rawReasons = isGuestMode
      ? guestReasons
      : (returnReasonsData?.returnReasons ?? []);
    return toLocalizedStorefrontReasonOptions(
      rawReasons,
      String(languageCode ?? ''),
    );
  }, [
    guestReasons,
    isGuestMode,
    returnReasonsData?.returnReasons,
    languageCode,
  ]);

  const registeredOutOfEuScopeBlocked =
    !isGuestMode &&
    Boolean(withdrawalEligibilityData?.withdrawalEligibility.withdrawal?.lines)
      ? withdrawalEligibilityData?.withdrawalEligibility.withdrawal?.lines.every(
          (l) => l.disabledReasonCode === 'OUT_OF_EU_SCOPE',
        )
      : false;

  const redirectOrderId =
    state.order?.id ?? (typeof orderId === 'string' ? orderId : '');

  useEffect(() => {
    if (!registeredOutOfEuScopeBlocked) return;
    if (!redirectOrderId) return;
    push(
      paths.myAccount.myOrders.orderDetails.url({
        orderId: redirectOrderId,
        query: { type: 'ORDERHISTORY' },
      }),
    );
  }, [
    paths.myAccount.myOrders.orderDetails,
    push,
    redirectOrderId,
    registeredOutOfEuScopeBlocked,
  ]);

  useEffect(() => {
    if (WITHDRAWAL_ENABLED) {
      return;
    }
    if (isGuestMode) {
      onGuestCancel?.();
      return;
    }
    if (!redirectOrderId) {
      return;
    }
    push(
      paths.myAccount.myOrders.orderDetails.url({
        orderId: redirectOrderId,
        query: { type: 'ORDERHISTORY' },
      }),
    );
  }, [
    isGuestMode,
    onGuestCancel,
    paths.myAccount.myOrders.orderDetails,
    push,
    redirectOrderId,
  ]);

  const orderIdForReturnState =
    state.order?.id ?? (typeof orderId === 'string' ? orderId : '');

  const returnState = useOrderReturnState({
    formatMessage,
    guest: {
      authContext: guestAuthContext,
      onCancel: onGuestCancel,
      onEligibilityChange: onGuestEligibilityChange,
    },
    isGuestMode,
    orderAppOrderId,
    orderId: orderIdForReturnState,
    reasonOptions,
    returnableLines,
    router: { paths, push },
    withdrawal: {
      eligibilityData: withdrawalEligibilityData,
      eligibilityOverride: withdrawalEligibilityOverride,
      isEligibilityLoading: isWithdrawalEligibilityLoading,
      refetchEligibility: refetchWithdrawalEligibility,
    },
  });

  return {
    baseFieldId,
    isWithdrawalFeatureDisabled,
    channelSlug,
    formatMessage,
    imageLoader,
    isGuestMode,
    isMobileView,
    isWithdrawalEligibilityLoading,
    languageCode,
    orderId,
    orderLines,
    paths,
    push,
    reasonOptions,
    registeredOutOfEuScopeBlocked,
    returnableLines,
    state,
    ...returnState,
  };
}
